/*
 * Purpose: React hook — subscription tier + guest gates for UI and actions
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import {
  canCreateMission,
  canRunFlows,
  canSaveFlows,
  canUseSubscriptionFeature,
  subscriptionFromState,
  subscriptionGateMessage,
  type SubscriptionAccess,
  type SubscriptionFeature,
} from '@/lib/subscriptionPolicy';
import { APP_ROUTES } from '@/lib/appRoutes';

export function useSubscriptionAccess(): SubscriptionAccess {
  const serverUserState = useAuthStore((s) => s.serverUserState);
  return useMemo(() => subscriptionFromState(serverUserState), [serverUserState]);
}

export function useSubscriptionGate() {
  const access = useSubscriptionAccess();
  const workflows = useWorkflowStore((s) => s.workflows);
  const activeMissionCount = workflows.length;

  return useMemo(
    () => ({
      access,
      canSave: canSaveFlows(access),
      canRun: canRunFlows(access),
      canCreate: canCreateMission(access, activeMissionCount),
      canUse: (feature: SubscriptionFeature) => canUseSubscriptionFeature(access, feature),
      message: (feature: SubscriptionFeature) => subscriptionGateMessage(feature, access),
      choosePlanHref: APP_ROUTES.pricing,
      isGuest: access.guestMode,
      tier: access.tier,
    }),
    [access, activeMissionCount]
  );
}
